<template>
  <BaseReport
    ref="baseReport"
    :loading="loading"
    :reportTitle="reportTitle"
    :reportSubtitle="dateRangeLabel"
    :columns="6"
    @mounted="loadData"
  >
    <template #controls>
      <div class="report-controls">
        <AnalyticsDateRangePicker
          :modelValue="{ startDate: rangeSelection.startDate, endDate: rangeSelection.endDate }"
          @update:modelValue="handleDateRangeChange"
        />
        <AnalyticsPeriodPicker
          v-model="rangeSelection.intervalType"
          @update:modelValue="handlePeriodChange"
        />
      </div>
    </template>

    <template #header>
      <h2>Financials</h2>
      <p>All metrics below reflect tickets created during the selected date range ({{ dateRangeLabel }})</p>
    </template>

    <template #content>
      <!-- Row 0: KPI Stats -->
      <CardKpi
        class="cell cell-kpi-1"
        title="Number of Tickets"
        :value="totalTickets"
        format="number"
        :description="ticketsDescription"
      />
      <CardKpi
        class="cell cell-kpi-2"
        title="Total Sales"
        :value="totalSalesAmount"
        format="currency"
        :description="salesDescription"
      />
      <CardKpi
        class="cell cell-kpi-3"
        title="Amount Collected"
        :value="totalCollectionsAmount"
        format="currency"
        :description="collectionsDescription"
      />

      <!-- Row 1: Sales (left) + Avg Ticket (right) -->
      <CardLineChart
        class="cell cell-sales-chart"
        title="Sales per Period"
        subtitle="New vs existing, per period"
        variant="stacked"
        :data="salesChartData"
        :options="chartOptions"
        :formatTooltipValue="formatCurrency"
        :height="300"
        @point-click="handleSalesChartClick"
      />

      <CardBarChart
        class="cell cell-avg-chart"
        title="Average Ticket Amount per Period"
        subtitle="New vs existing, per period"
        variant="grouped"
        :data="avgTicketChartData"
        :options="barChartOptions"
        :height="300"
        @bar-click="handleAvgChartClick"
      />

      <!-- Row 2: Collections (left) + Avg Initial Payment (right) -->
      <CardLineChart
        class="cell cell-collections-chart"
        title="Collections per Period"
        subtitle="New vs existing, per period"
        variant="stacked"
        :data="collectionsChartData"
        :options="chartOptions"
        :formatTooltipValue="formatCurrency"
        :height="300"
        @point-click="handleCollectionsChartClick"
      />

      <CardBarChart
        class="cell cell-avg-initial-chart"
        title="Avg Amount Down per Period"
        subtitle="Avg upfront payment, per period"
        variant="grouped"
        :data="avgInitialPaymentChartData"
        :options="barChartOptions"
        :height="300"
        @bar-click="handleAvgChartClick"
      />

      <!-- Row 3: Detailed Table -->
      <CardTable
        class="cell cell-table"
        title="Period Breakdown"
        subtitle="Click row to drill down"
        :columns="tableColumns"
        :rows="tableRows"
        variant="summary"
        :summaryRow="summaryRow"
        :maxRows="100"
        :clickable="true"
        :stickyFirstColumn="true"
        @row-click="handleTableRowClick"
      />
    </template>
  </BaseReport>
</template>

<script>
import BaseReport from '@/components/common/BaseReport.vue';
import AnalyticsDateRangePicker from '@/components/inputs/AnalyticsDateRangePicker.vue';
import AnalyticsPeriodPicker from '@/components/inputs/AnalyticsPeriodPicker.vue';
import CardLineChart from '@/components/common/CardLineChart.vue';
import CardBarChart from '@/components/common/CardBarChart.vue';
import CardTable from '@/components/common/CardTable.vue';
import CardKpi from '@/components/common/CardKpi.vue';
import * as api from '@/api';
import { formatDateShort } from '@/utils/formatters';
import { serializeFilters } from '@/utils/filterSerializer';

export default {
  name: 'FinancialsReport',
  components: {
    BaseReport,
    AnalyticsDateRangePicker,
    AnalyticsPeriodPicker,
    CardLineChart,
    CardBarChart,
    CardTable,
    CardKpi
  },
  emits: ['loading'],
  data() {
    return {
      reportTitle: 'Financials',
      loading: true,
      rangeSelection: {
        startDate: null,
        endDate: null,
        intervalType: 'weekly'
      },
      periodData: []
    };
  },
  mounted() {
    this.loadData();
  },
  computed: {
    dateRangeLabel() {
      if (!this.rangeSelection.startDate || !this.rangeSelection.endDate) {
        return '';
      }
      const from = formatDateShort(this.rangeSelection.startDate);
      const to = formatDateShort(this.rangeSelection.endDate);
      return `${from} - ${to}`;
    },
    blueColors() {
      return {
        existingPatient: '#4338ca', // indigo-700 (darker)
        newPatient: '#818cf8'       // indigo-400 (lighter)
      };
    },
    salesChartData() {
      if (!this.periodData || this.periodData.length === 0) {
        return null;
      }
      return {
        labels: this.periodData.map(p => p.label),
        datasets: [
          {
            label: 'Existing Patient',
            data: this.periodData.map(p => p.existingPatientSalesCents / 100),
            color: this.blueColors.existingPatient
          },
          {
            label: 'New Patient',
            data: this.periodData.map(p => p.newPatientSalesCents / 100),
            color: this.blueColors.newPatient
          }
        ]
      };
    },
    collectionsChartData() {
      if (!this.periodData || this.periodData.length === 0) {
        return null;
      }
      return {
        labels: this.periodData.map(p => p.label),
        datasets: [
          {
            label: 'Existing Patient',
            data: this.periodData.map(p => p.existingPatientCollectionsCents / 100),
            color: this.blueColors.existingPatient
          },
          {
            label: 'New Patient',
            data: this.periodData.map(p => p.newPatientCollectionsCents / 100),
            color: this.blueColors.newPatient
          }
        ]
      };
    },
    avgTicketChartData() {
      if (!this.periodData || this.periodData.length === 0) {
        return null;
      }
      return {
        labels: this.periodData.map(p => p.label),
        datasets: [
          {
            label: 'Existing Patient Avg',
            data: this.periodData.map(p => p.existingPatientAvgTicketCents / 100),
            color: this.blueColors.existingPatient
          },
          {
            label: 'New Patient Avg',
            data: this.periodData.map(p => p.newPatientAvgTicketCents / 100),
            color: this.blueColors.newPatient
          }
        ]
      };
    },
    avgInitialPaymentChartData() {
      if (!this.periodData || this.periodData.length === 0) {
        return null;
      }
      return {
        labels: this.periodData.map(p => p.label),
        datasets: [
          {
            label: 'Existing Patient Avg',
            data: this.periodData.map(p => p.existingPatientAvgInitialPaymentCents / 100),
            color: this.blueColors.existingPatient
          },
          {
            label: 'New Patient Avg',
            data: this.periodData.map(p => p.newPatientAvgInitialPaymentCents / 100),
            color: this.blueColors.newPatient
          }
        ]
      };
    },
    chartOptions() {
      return {
        yAxis: {
          type: 'value',
          axisLabel: {
            formatter: (value) => this.formatCurrencyShort(value)
          }
        }
      };
    },
    barChartOptions() {
      return {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          },
          formatter: (params) => {
            let result = `<div style="font-weight: 600; margin-bottom: 8px;">${params[0].axisValue}</div>`;
            params.forEach(param => {
              const value = param.value || 0;
              result += `<div style="display: flex; align-items: center; gap: 8px; margin: 4px 0;">
                <span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: ${param.color};"></span>
                <span>${param.seriesName}: ${this.formatCurrency(value)}</span>
              </div>`;
            });
            return result;
          }
        },
        yAxis: {
          type: 'value',
          axisLabel: {
            formatter: (value) => this.formatCurrencyShort(value)
          }
        }
      };
    },
    tableColumns() {
      return [
        { key: 'period', label: 'Period' },
        { key: 'npSales', label: 'NP Sales', format: 'currency' },
        { key: 'epSales', label: 'EP Sales', format: 'currency' },
        { key: 'totalSales', label: 'Total Sales', format: 'currency' },
        { key: 'npCollections', label: 'NP Collections', format: 'currency' },
        { key: 'epCollections', label: 'EP Collections', format: 'currency' },
        { key: 'totalCollections', label: 'Total Collections', format: 'currency' },
        { key: 'npAvg', label: 'NP Avg', format: 'currency' },
        { key: 'epAvg', label: 'EP Avg', format: 'currency' }
      ];
    },
    tableRows() {
      return this.periodData.map(p => ({
        period: p.label,
        start: p.start,
        end: p.end,
        npSales: p.newPatientSalesCents / 100,
        epSales: p.existingPatientSalesCents / 100,
        totalSales: (p.newPatientSalesCents + p.existingPatientSalesCents) / 100,
        npCollections: p.newPatientCollectionsCents / 100,
        epCollections: p.existingPatientCollectionsCents / 100,
        totalCollections: (p.newPatientCollectionsCents + p.existingPatientCollectionsCents) / 100,
        npAvg: p.newPatientAvgTicketCents / 100,
        epAvg: p.existingPatientAvgTicketCents / 100
      }));
    },
    summaryRow() {
      if (this.tableRows.length === 0) return null;
      const totals = this.tableRows.reduce((acc, row) => {
        acc.npSales += row.npSales;
        acc.epSales += row.epSales;
        acc.totalSales += row.totalSales;
        acc.npCollections += row.npCollections;
        acc.epCollections += row.epCollections;
        acc.totalCollections += row.totalCollections;
        acc.npTickets += this.periodData.find(p => p.label === row.period)?.newPatientTickets || 0;
        acc.epTickets += this.periodData.find(p => p.label === row.period)?.existingPatientTickets || 0;
        return acc;
      }, {
        npSales: 0,
        epSales: 0,
        totalSales: 0,
        npCollections: 0,
        epCollections: 0,
        totalCollections: 0,
        npTickets: 0,
        epTickets: 0
      });

      return {
        period: 'Total',
        npSales: totals.npSales,
        epSales: totals.epSales,
        totalSales: totals.totalSales,
        npCollections: totals.npCollections,
        epCollections: totals.epCollections,
        totalCollections: totals.totalCollections,
        npAvg: totals.npTickets > 0 ? totals.npSales / totals.npTickets : 0,
        epAvg: totals.epTickets > 0 ? totals.epSales / totals.epTickets : 0
      };
    },
    totalTickets() {
      return this.periodData.reduce((sum, p) => {
        return sum + (p.newPatientTickets || 0) + (p.existingPatientTickets || 0);
      }, 0);
    },
    npTickets() {
      return this.periodData.reduce((sum, p) => sum + (p.newPatientTickets || 0), 0);
    },
    epTickets() {
      return this.periodData.reduce((sum, p) => sum + (p.existingPatientTickets || 0), 0);
    },
    ticketsDescription() {
      if (this.totalTickets === 0) return '';
      return `${this.npTickets} new patient, ${this.epTickets} existing patient tickets in this period`;
    },
    totalSalesAmount() {
      return this.periodData.reduce((sum, p) => {
        return sum + (p.newPatientSalesCents || 0) + (p.existingPatientSalesCents || 0);
      }, 0) / 100;
    },
    npSalesAmount() {
      return this.periodData.reduce((sum, p) => sum + (p.newPatientSalesCents || 0), 0) / 100;
    },
    epSalesAmount() {
      return this.periodData.reduce((sum, p) => sum + (p.existingPatientSalesCents || 0), 0) / 100;
    },
    salesDescription() {
      if (this.totalSalesAmount === 0) return '';
      return `${this.formatCurrencyShort(this.npSalesAmount)} from new patients, ${this.formatCurrencyShort(this.epSalesAmount)} from existing patients`;
    },
    totalCollectionsAmount() {
      return this.periodData.reduce((sum, p) => {
        return sum + (p.newPatientCollectionsCents || 0) + (p.existingPatientCollectionsCents || 0);
      }, 0) / 100;
    },
    npCollectionsAmount() {
      return this.periodData.reduce((sum, p) => sum + (p.newPatientCollectionsCents || 0), 0) / 100;
    },
    epCollectionsAmount() {
      return this.periodData.reduce((sum, p) => sum + (p.existingPatientCollectionsCents || 0), 0) / 100;
    },
    collectionsDescription() {
      if (this.totalCollectionsAmount === 0) return '';
      return `${this.formatCurrencyShort(this.npCollectionsAmount)} from new patients, ${this.formatCurrencyShort(this.epCollectionsAmount)} from existing patients`;
    }
  },
  methods: {
    handleDateRangeChange(dateRange) {
      this.rangeSelection.startDate = dateRange.startDate;
      this.rangeSelection.endDate = dateRange.endDate;
      this.loading = true;
      this.periodData = [];
      this.$emit('loading', true);
      this.loadData();
    },
    handlePeriodChange() {
      this.loading = true;
      this.periodData = [];
      this.$emit('loading', true);
      this.loadData();
    },
    async loadData() {
      if (!this.rangeSelection.startDate || !this.rangeSelection.endDate) {
        return;
      }

      try {
        const data = await api.fetchFinancialsTrend({
          startDate: this.rangeSelection.startDate,
          endDate: this.rangeSelection.endDate,
          intervalType: this.rangeSelection.intervalType
        });

        this.periodData = data.periods;
      } catch (error) {
        console.error('Error loading financials trend:', error);
        this.periodData = [];
      }

      this.loading = false;
      this.$emit('loading', false);
    },
    handleSalesChartClick(event) {
      const { dataIndex, seriesName } = event;
      const period = this.periodData[dataIndex];
      if (period) {
        // Stacked chart shows split by ticket type, so filter by the clicked series
        const ticketType = this.getTicketTypeFromSeriesName(seriesName);
        this.navigateToTickets(period.start, period.end, ticketType);
      }
    },
    handleCollectionsChartClick(event) {
      const { dataIndex, seriesName } = event;
      const period = this.periodData[dataIndex];
      if (period) {
        // Stacked chart shows split by ticket type, so filter by the clicked series
        const ticketType = this.getTicketTypeFromSeriesName(seriesName);
        this.navigateToPayments(period.start, period.end, ticketType);
      }
    },
    handleAvgChartClick(event) {
      const { dataIndex } = event;
      const period = this.periodData[dataIndex];
      if (period) {
        // Grouped chart shows both together, no ticket type filter
        this.navigateToTickets(period.start, period.end);
      }
    },
    getTicketTypeFromSeriesName(seriesName) {
      if (!seriesName) return null;
      if (seriesName.toLowerCase().includes('new')) return 'new_patient';
      if (seriesName.toLowerCase().includes('existing')) return 'existing_patient';
      return null;
    },
    handleTableRowClick(row) {
      if (row.start && row.end) {
        this.navigateToTickets(row.start, row.end);
      }
    },
    navigateToTickets(startDate, endDate, ticketType = null) {
      const filters = [
        {
          field: 'dateTicketStart',
          operator: 'between',
          from: startDate,
          to: endDate
        }
      ];

      if (ticketType) {
        filters.push({
          field: 'ticketType',
          operator: 'equals',
          value: ticketType
        });
      }

      const serializedFilters = serializeFilters(filters);
      this.$router.push({
        path: '/tickets',
        query: { filters: serializedFilters }
      });
    },
    navigateToPayments(startDate, endDate, ticketType = null) {
      const filters = [
        {
          field: 'datePaid',
          operator: 'between',
          from: startDate,
          to: endDate
        }
      ];

      if (ticketType) {
        filters.push({
          field: 'ticketType',
          operator: 'equals',
          value: ticketType
        });
      }

      const serializedFilters = serializeFilters(filters);
      this.$router.push({
        path: '/payments',
        query: { filters: serializedFilters }
      });
    },
    formatCurrency(value) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(value);
    },
    formatCurrencyShort(value) {
      if (value >= 1000000) {
        return '$' + (value / 1000000).toFixed(1) + 'M';
      } else if (value >= 1000) {
        return '$' + (value / 1000).toFixed(0) + 'K';
      }
      return '$' + value.toFixed(2);
    },
    async generatePDF() {
      this.$refs.baseReport.generateReportPDF({
        title: 'Financials',
        subtitle: this.dateRangeLabel,
        kpis: [
          { label: 'Number of Tickets', value: this.totalTickets, format: 'number' },
          { label: 'Total Sales', value: this.totalSalesAmount, format: 'currency' },
          { label: 'Amount Collected', value: this.totalCollectionsAmount, format: 'currency' }
        ],
        columns: this.tableColumns,
        rows: this.tableRows,
        summaryRow: this.summaryRow
      });
    }
  }
};
</script>

<style scoped>
/* Report controls layout */
.report-controls {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

/* Grid cell positioning */
.cell {
  background: #fff;
  min-width: 0; /* Prevent grid blowout */
}

/* Row 0: KPI Stats */
.cell-kpi-1 { grid-column: span 2; }
.cell-kpi-2 { grid-column: span 2; }
.cell-kpi-3 { grid-column: span 2; }

/* Row 1: Sales + Avg Ticket split evenly (50% each) */
.cell-sales-chart { grid-column: span 3; }
.cell-avg-chart { grid-column: span 3; }

/* Row 2: Collections + Avg Initial Payment split evenly (50% each) */
.cell-collections-chart { grid-column: span 3; }
.cell-avg-initial-chart { grid-column: span 3; }

/* Row 3: Table full width with fixed-width columns */
.cell-table { grid-column: span 6; }

.cell-table :deep(.data-table th),
.cell-table :deep(.data-table td) {
  width: 200px;
  min-width: 200px;
  max-width: 200px;
}

/* Mobile: stack all cells */
@media (max-width: 768px) {
  .cell-kpi-1,
  .cell-kpi-2,
  .cell-kpi-3,
  .cell-sales-chart,
  .cell-avg-chart,
  .cell-collections-chart,
  .cell-avg-initial-chart,
  .cell-table {
    grid-column: span 6;
  }
}
</style>
